import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { AuthTokenDto } from '@workers-benefit-fund/api'

import {
  AuthState,
  AuthStatus,
  LocalStorageAuthSession,
  LocalStoragePendingPasskeyStep,
  PendingPasskeyStep,
} from '../auth.types'
import { isCookieAuthenticationEnabled } from '../utils/isCookieAuthenticationEnabled'

import { SESSION_CONFIG } from '@v2/commons/config/session.config'

const authStorage = typeof localStorage === 'undefined' ? null : localStorage

interface SetAuthInfoPayload extends Partial<AuthTokenDto> {
  organizationId?: string
  membershipId?: string
}

const initialState: AuthState = {
  accessToken: '',
  refreshToken: '',
  userId: '',
  organizationId: '',
  membershipId: '',
  status: isCookieAuthenticationEnabled ? 'unknown' : 'unauthenticated',
  lastExplicitLoginAt: 0,
  pendingPasskeyStep: null,
}

const PENDING_PASSKEY_STEPS: PendingPasskeyStep[] = [
  'SET_UP_PASSKEY',
  'VERIFY_PASSKEY',
  'EXPIRED_PASSKEY',
]

const readPendingPasskeyStep = (): PendingPasskeyStep | null => {
  try {
    const step = authStorage?.getItem(LocalStoragePendingPasskeyStep)
    return PENDING_PASSKEY_STEPS.find((known) => known === step) ?? null
  } catch {
    return null
  }
}

const writePendingPasskeyStep = (step: PendingPasskeyStep | null) => {
  try {
    if (step) authStorage?.setItem(LocalStoragePendingPasskeyStep, step)
    else authStorage?.removeItem(LocalStoragePendingPasskeyStep)
  } catch {
    // The in-memory step still guards the current tab.
  }
}

const getInitialState = (): AuthState => ({
  ...getInitialSession(),
  pendingPasskeyStep: readPendingPasskeyStep(),
})

const getInitialSession = (): AuthState => {
  if (isCookieAuthenticationEnabled) {
    // A cookie session is verified during application bootstrap. Do not retain
    // any legacy JavaScript-visible credentials when the feature is enabled.
    try {
      authStorage?.removeItem(LocalStorageAuthSession)
    } catch {
      // Cookie authentication does not rely on browser storage.
    }
    return initialState
  }

  try {
    const persistedState = authStorage?.getItem(LocalStorageAuthSession)
    if (!persistedState) {
      return initialState
    }

    const parsedState: unknown = JSON.parse(persistedState)
    if (!parsedState || typeof parsedState !== 'object') {
      throw new Error('Invalid legacy auth session')
    }

    const session = parsedState as Record<string, unknown>
    const credential = (key: string) =>
      typeof session[key] === 'string' ? session[key] : ''
    const accessToken = credential('accessToken')
    const refreshToken = credential('refreshToken')

    if (!accessToken && !refreshToken) {
      authStorage?.removeItem(LocalStorageAuthSession)
      return initialState
    }

    return {
      ...initialState,
      accessToken,
      refreshToken,
      userId: credential('userId'),
      organizationId: credential('organizationId'),
      membershipId: credential('membershipId'),
      status: 'unknown',
    }
  } catch {
    try {
      authStorage?.removeItem(LocalStorageAuthSession)
    } catch {
      // An inaccessible store is treated as an absent legacy session.
    }
    return initialState
  }
}

const authSchema = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    recordExplicitLogin: (state, action: PayloadAction<number>) => {
      state.lastExplicitLoginAt = action.payload
    },
    setAuthInfo: (state, action: PayloadAction<SetAuthInfoPayload>) => {
      const {
        access_token,
        refresh_token,
        userId,
        organizationId,
        membershipId,
      } = action.payload

      if (access_token && !isCookieAuthenticationEnabled) {
        state.accessToken = access_token
      }

      if (refresh_token && !isCookieAuthenticationEnabled) {
        state.refreshToken = refresh_token
      }

      if (userId) {
        state.userId = userId
      }

      if (organizationId) {
        state.organizationId = organizationId
      }

      if (membershipId) {
        state.membershipId = membershipId
      }

      if (access_token || userId) {
        state.status = 'authenticated'
        authStorage?.setItem(
          SESSION_CONFIG.STORAGE_KEYS.TOKEN_ISSUED_AT,
          String(Date.now()),
        )
      }

      if (!isCookieAuthenticationEnabled) {
        authStorage?.setItem(
          LocalStorageAuthSession,
          JSON.stringify({
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
            userId: state.userId,
            organizationId: state.organizationId,
            membershipId: state.membershipId,
            status: state.status,
          }),
        )
      }
    },
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.status = action.payload
    },
    setPendingPasskeyStep: (
      state,
      action: PayloadAction<PendingPasskeyStep | null>,
    ) => {
      state.pendingPasskeyStep = action.payload
      writePendingPasskeyStep(action.payload)
    },
    resetAuthStore: (state) => {
      state.accessToken = ''
      state.refreshToken = ''
      state.userId = ''
      state.organizationId = ''
      state.membershipId = ''
      state.status = 'unauthenticated'
      state.pendingPasskeyStep = null
      authStorage?.removeItem(LocalStorageAuthSession)
      writePendingPasskeyStep(null)
    },
  },
})

export const {
  recordExplicitLogin,
  setAuthInfo,
  setAuthStatus,
  setPendingPasskeyStep,
  resetAuthStore,
} = authSchema.actions

export default authSchema.reducer
